/**
 * `RowKind` — what `RowKind.of(row)` answers: a rows-side row's variant,
 * named, with the row typed as that variant's **reads**. The checked way to
 * narrow a row outside this package: the variant classes are types on
 * `@tento/lona/sheets` (no `instanceof` against them), and a row a sheet's
 * reads hand out is a reads face that no class check would match anyway.
 *
 * Two levels, so a caller asks the question it has: `kind` is the most
 * specific variant this answers (`"calendar-alias"`, `"column-timezone"`,
 * …), `role` the role base it extends (`"alias"`, `"source"`, `"layout"`,
 * `"renderer"`, or `"other"` for every other row). `k.role === "alias"`
 * narrows `k.row` to `AliasRowReads`; `k.kind === "calendar-alias"` to
 * `CalendarAliasRowReads`.
 *
 * `k.row` is the row's frozen reads face (`ModelRowReads` plus its variant's
 * reads — the same face a sheet's reads hand out for the row), never the live
 * row: no writer is reachable from it, and a facade it resolves
 * (`k.row.as(TASKS_FACADE)`) carries reads only. An app writes a row through
 * the SDK row's verbs (its writable facade's `write` / `clear`, `row.alias()?.setProp`) and the
 * domain clients (`client.tasks`, `client.events`).
 *
 * ```ts
 * const k = RowKind.of(row);
 * if (k.role === "alias") label = k.row.displayName;
 * ```
 *
 * A renderer implementor that dispatches on the concrete variants imports
 * the classes from `@tento/lona/sheets/platform`; the SDK's own front-ends
 * take the live row through `liveRowOf` (`@tento-lona/sheets/internal-ui`).
 */

import type { Option } from "../core/option.ts";
import type { ModelRowReads } from "./model-row-reads.ts";
import { baseFaceOf, recordFace, withFaceMembers } from "./model-row-reads.ts";
import { liveRowOf } from "./model-row.ts";
import type { ModelRow } from "./model-row.ts";
import { AliasRow } from "./variants/alias-row.ts";
import { CalendarAliasRow } from "./variants/calendar-alias-row.ts";
import { ColumnNumberRow } from "./variants/column-number.ts";
import { FormulaDataSource } from "./variants/formula-data-source.ts";
import { LayoutRow } from "./variants/layout-row.ts";
import { RendererRow } from "./variants/renderer-row.ts";
import { SourceRow } from "./variants/source-row.ts";
import { ColumnTimezoneRow } from "./variants/column-timezone.ts";
import { ColumnWeatherRow } from "./variants/column-weather.ts";

/**
 * A source row's reads: its cells (`peekCells`), shape, whether
 * its cells take writes, and its facades (`as(key)`, reads only).
 */
export type SourceRowReads =
  & ModelRowReads
  & Pick<SourceRow, "descriptor" | "shape" | "cellsWritable" | "cellsDerived" | "anyStaleInRange" | "as">;

/** A formula source row's reads: a source row's, its expression and computed values. */
export type FormulaDataSourceReads = SourceRowReads & Pick<FormulaDataSource, "expression" | "peekValue">;

/** An alias row's reads: its template instance and display name. */
export type AliasRowReads = ModelRowReads & Pick<AliasRow, "descriptor" | "instance" | "displayName">;

/** A calendar alias row's reads: an alias row's, and its calendar view settings. */
export type CalendarAliasRowReads =
  & AliasRowReads
  & Pick<
    CalendarAliasRow,
    "hoursRange" | "calendarType" | "timezone" | "disableMonthlyView" | "hideAllDayEvents" | "effectiveView"
  >;

/** A layout row's reads: its descriptor and its layout config. */
export type LayoutRowReads = ModelRowReads & Pick<LayoutRow, "descriptor" | "config">;

/** A renderer row's reads: its descriptor and its renderer attributes. */
export type RendererRowReads = ModelRowReads & Pick<RendererRow, "descriptor" | "attrs">;

/** A `column.number` renderer's reads: its source row (as reads), number reads and format. */
export type ColumnNumberRowReads =
  & RendererRowReads
  & Pick<ColumnNumberRow, "editable" | "peekNumber" | "unit" | "format" | "prompt">
  & { readonly source: SourceRowReads };

/** A `column.timezone` renderer's reads: its source row (as reads) and its zone. */
export type ColumnTimezoneRowReads =
  & RendererRowReads
  & Pick<ColumnTimezoneRow, "tz">
  & { readonly source: SourceRowReads };

/** A `column.weather` renderer's reads: its source row (as reads) and its location id. */
export type ColumnWeatherRowReads =
  & RendererRowReads
  & Pick<ColumnWeatherRow, "lid">
  & { readonly source: SourceRowReads };

/** A rows-side row's variant, as `RowKind.of` answers it (see the module note). */
export type RowKind =
  | { readonly kind: "calendar-alias"; readonly role: "alias"; readonly row: CalendarAliasRowReads }
  | { readonly kind: "alias"; readonly role: "alias"; readonly row: AliasRowReads }
  | { readonly kind: "formula-source"; readonly role: "source"; readonly row: FormulaDataSourceReads }
  | { readonly kind: "source"; readonly role: "source"; readonly row: SourceRowReads }
  | { readonly kind: "layout"; readonly role: "layout"; readonly row: LayoutRowReads }
  | { readonly kind: "column-number"; readonly role: "renderer"; readonly row: ColumnNumberRowReads }
  | { readonly kind: "column-timezone"; readonly role: "renderer"; readonly row: ColumnTimezoneRowReads }
  | { readonly kind: "column-weather"; readonly role: "renderer"; readonly row: ColumnWeatherRowReads }
  | { readonly kind: "renderer"; readonly role: "renderer"; readonly row: RendererRowReads }
  | { readonly kind: "other"; readonly role: "other"; readonly row: ModelRowReads };

/** Types and values that go with `RowKind`: `of`, `Name`, `Role`, `RowOf`. */
export namespace RowKind {
  /**
   * **The variant of `row`** — the checked way to narrow a row: `kind`
   * names the most specific variant, `role` the role base, and `row` is the
   * row's reads face typed as that variant's reads (`SourceRowReads`,
   * `AliasRowReads`, …) — never the live row, so no writer is reachable
   * from it. Takes a row as any read hands it out (a sheet reads' row face,
   * or a live row an implementor holds). The variant classes are
   * `@tento/lona/sheets/platform` names, so this, not `instanceof`, is how
   * an app tells an alias from a source row.
   */
  export function of(row: ModelRowReads): RowKind {
    return kindOfLiveRow(liveRowOf(row));
  }

  /** A `RowKind`'s `kind`: the most specific variant `of` names. */
  export type Name = RowKind["kind"];
  /** A `RowKind`'s `role`: the role base the variant extends. */
  export type Role = RowKind["role"];
  /** The reads face `kind` names (`RowKind.RowOf<"alias">` is `AliasRowReads`). */
  export type RowOf<K extends Name> = Extract<RowKind, { readonly kind: K }>["row"];
}

/** Every row's kind (`kindOfLiveRow`), keyed by its live row: one face per row. */
const kindsByRow = new WeakMap<ModelRow, RowKind>();

/**
 * The kind of a live row (`RowKind`): its variant, named, with its frozen
 * reads face — built once per row, so every read of the row hands out the
 * same face. `RowKind.of` answers through it, and the variants barrel
 * installs its `row` as `rowReadsOf`'s face (`installRowFaces`).
 */
export function kindOfLiveRow(row: ModelRow): RowKind {
  const cached = kindsByRow.get(row);
  if (cached != null) return cached;
  const kind = rowKindOf(row, baseFaceOf(row));
  Object.freeze(kind.row);
  Object.freeze(kind);
  kindsByRow.set(row, kind);
  recordFace(kind.row, row);
  return kind;
}

/**
 * The kind of a live row, its most specific variant first, with `base` (the
 * row's `ModelRowReads` face, not yet frozen) extended by the variant's
 * reads. `kindOfLiveRow` caches it per row.
 */
export function rowKindOf(row: ModelRow, base: ModelRowReads): RowKind {
  if (row instanceof CalendarAliasRow) {
    return { kind: "calendar-alias", role: "alias", row: withFaceMembers(aliasFace(row, base), calendarAliasMembers(row)) };
  }
  if (row instanceof AliasRow) return { kind: "alias", role: "alias", row: aliasFace(row, base) };
  if (row instanceof FormulaDataSource) {
    return { kind: "formula-source", role: "source", row: withFaceMembers(sourceFace(row, base), formulaMembers(row)) };
  }
  if (row instanceof SourceRow) return { kind: "source", role: "source", row: sourceFace(row, base) };
  if (row instanceof LayoutRow) {
    const members: Pick<LayoutRow, "descriptor" | "config"> = { descriptor: row.descriptor, config: () => row.config() };
    return { kind: "layout", role: "layout", row: withFaceMembers(base, members) };
  }
  if (row instanceof ColumnNumberRow) {
    return { kind: "column-number", role: "renderer", row: withFaceMembers(rendererFace(row, base), columnNumberMembers(row)) };
  }
  if (row instanceof ColumnTimezoneRow) {
    const members: Pick<ColumnTimezoneRow, "tz"> & { readonly source: SourceRowReads } = {
      get tz() {
        return row.tz;
      },
      get source() {
        return sourceRowReadsOf(row.source);
      },
    };
    return { kind: "column-timezone", role: "renderer", row: withFaceMembers(rendererFace(row, base), members) };
  }
  if (row instanceof ColumnWeatherRow) {
    const members: Pick<ColumnWeatherRow, "lid"> & { readonly source: SourceRowReads } = {
      get lid() {
        return row.lid;
      },
      get source() {
        return sourceRowReadsOf(row.source);
      },
    };
    return { kind: "column-weather", role: "renderer", row: withFaceMembers(rendererFace(row, base), members) };
  }
  if (row instanceof RendererRow) return { kind: "renderer", role: "renderer", row: rendererFace(row, base) };
  return { kind: "other", role: "other", row: base };
}

/**
 * A source row's reads face (the source row a renderer reads, a list
 * aggregate's constituents): its `RowKind` face, typed as `SourceRowReads`.
 * Throws for a row that is not a source row.
 */
export function sourceRowReadsOf(row: SourceRow): SourceRowReads {
  const kind = kindOfLiveRow(row);
  if (kind.role !== "source") throw new TypeError(`RowKind: row ${row.id.rawId} is not a source row`);
  return kind.row;
}

function sourceFace(row: SourceRow, base: ModelRowReads): SourceRowReads {
  const members: Pick<SourceRow, "descriptor" | "shape" | "cellsWritable" | "cellsDerived" | "anyStaleInRange" | "as"> = {
    descriptor: row.descriptor,
    get shape(): Option<string> {
      return row.shape;
    },
    get cellsWritable(): boolean {
      return row.cellsWritable;
    },
    get cellsDerived(): boolean {
      return row.cellsDerived;
    },
    anyStaleInRange: (range) => row.anyStaleInRange(range),
    as: (key) => row.as(key),
  };
  return withFaceMembers(base, members);
}

function formulaMembers(row: FormulaDataSource): Pick<FormulaDataSource, "expression" | "peekValue"> {
  return {
    get expression(): string {
      return row.expression;
    },
    peekValue: (date) => row.peekValue(date),
  };
}

function aliasFace(row: AliasRow, base: ModelRowReads): AliasRowReads {
  const members: Pick<AliasRow, "descriptor" | "instance" | "displayName"> = {
    descriptor: row.descriptor,
    instance: () => row.instance(),
    get displayName(): string {
      return row.displayName;
    },
  };
  return withFaceMembers(base, members);
}

function calendarAliasMembers(
  row: CalendarAliasRow,
): Pick<
  CalendarAliasRow,
  "hoursRange" | "calendarType" | "timezone" | "disableMonthlyView" | "hideAllDayEvents" | "effectiveView"
> {
  return {
    hoursRange: () => row.hoursRange(),
    get calendarType() {
      return row.calendarType;
    },
    get timezone() {
      return row.timezone;
    },
    get disableMonthlyView(): boolean {
      return row.disableMonthlyView;
    },
    get hideAllDayEvents(): boolean {
      return row.hideAllDayEvents;
    },
    effectiveView: (sheetTimescale) => row.effectiveView(sheetTimescale),
  };
}

function rendererFace(row: RendererRow, base: ModelRowReads): RendererRowReads {
  const members: Pick<RendererRow, "descriptor" | "attrs"> = {
    descriptor: row.descriptor,
    attrs: () => row.attrs(),
  };
  return withFaceMembers(base, members);
}

function columnNumberMembers(
  row: ColumnNumberRow,
): Pick<ColumnNumberRow, "editable" | "peekNumber" | "unit" | "format" | "prompt"> & {
  readonly source: SourceRowReads;
} {
  return {
    get editable(): boolean {
      return row.editable;
    },
    peekNumber: (date) => row.peekNumber(date),
    get unit() {
      return row.unit;
    },
    get format() {
      return row.format;
    },
    get prompt() {
      return row.prompt;
    },
    get source() {
      return sourceRowReadsOf(row.source);
    },
  };
}
